import { jsx, jsxs } from "react/jsx-runtime";
import { forwardRef } from "react";
import EzLabel from "../EzLabel/EzLabel.es.js";
import { InsetIcon, ErrorTriangle } from "../Icons/index.es.js";
import stitches from "../theme.config.es.js";
import EzAutosuggest from "./EzAutosuggest.es.js";
import EzDateInput from "./EzDateInput.es.js";
import EzSelect from "./EzSelect.es.js";
import EzTextArea from "./EzTextArea.es.js";
import EzTextInput, { CustomInput } from "./EzTextInput.es.js";
import EzTimeInput from "./EzTimeInput.es.js";
import useFocus from "../../utils/hooks/useFocus.es.js";
import useHover from "../../utils/hooks/useHover.es.js";
import useInput from "../../utils/hooks/useInput.es.js";
import { clsx } from "../../utils/mergeProps.es.js";
import { wrapEvents } from "../../utils/wrapEvent.es.js";
import { domProps } from "../../utils/domProps.es.js";
import { useId } from "@radix-ui/react-id";
const inputElements = ["text", "number", "password", "email"];
const inlineElements = [...inputElements];
const errorColor = stitches.css({ color: "$negative", fill: "$negative" });
const ErrorIcon = () => /* @__PURE__ */ jsx(InsetIcon, { insetY0: true, right0: true, pr2: true, children: /* @__PURE__ */ jsx(ErrorTriangle, { className: errorColor() }) });
const EzCustomInput = forwardRef(function EzCustomInput2({ type: Input, ...props }, ref) {
  return /* @__PURE__ */ jsx(CustomInput, { ...props, children: /* @__PURE__ */ jsx(Input, { ref, ...domProps(props) }) });
});
const resolveInputFromType = (type) => {
  if (type === "date") return EzDateInput;
  if (type === "select") return EzSelect;
  if (type === "autosuggest" || type === "autosuggest-search") return EzAutosuggest;
  if (type === "time") return EzTimeInput;
  if (type === "textarea") return EzTextArea;
  if (inputElements.includes(type)) return EzTextInput;
  return EzCustomInput;
};
const field = stitches.css({
  position: "relative",
  border: "none",
  padding: "0",
  variants: {
    labelSize: {
      small: { "& > * + *": { marginTop: "$50" } },
      normal: { "& > * + *": { marginTop: "$100" } }
    }
  },
  defaultVariants: { labelSize: "normal" }
});
const helper = stitches.css({
  fontFamily: "$defaultFont",
  fontSize: "$75",
  color: "$deemphasisText",
  marginTop: "$100"
});
const errorContainer = stitches.css({
  display: "flex",
  fontFamily: "$defaultFont",
  justifyContent: "flex-end",
  position: "relative",
  pointerEvents: "none"
});
const errorCallout = stitches.css({
  backgroundColor: "$negative",
  color: "white",
  fontSize: "$75",
  padding: "$100 $150",
  position: "relative",
  flexGrow: 1,
  right: 0,
  "@medium": {
    position: "absolute",
    userSelect: "none",
    zIndex: 1,
    boxShadow: "0 2px 4px 0 rgba(34, 36, 38, 0.12), 0 2px 10px 0 rgba(34, 36, 38, 0.15)",
    borderRadius: 3
  },
  variants: {
    active: { false: { "@medium": { srOnly: null } } },
    inline: {
      true: {
        borderBottomRightRadius: 3,
        borderBottomLeftRadius: 3,
        "@medium": { transform: "translate3d(0, -2px, 0)" }
      },
      false: {
        boxShadow: "0 2px 4px 0 rgba(34, 36, 38, 0.12), 0 2px 10px 0 rgba(34, 36, 38, 0.15)",
        borderRadius: 3,
        marginBottom: "0.5rem",
        transform: "translate3d(0, 6px, 0)"
      }
    }
  }
});
const arrow = stitches.css({
  "&::before": {
    content: "''",
    display: "block",
    width: "0px",
    height: "0px",
    borderLeft: "5px solid transparent",
    borderRight: "5px solid transparent",
    borderBottom: "5px solid $negative",
    position: "absolute",
    top: "-5px",
    right: "10px"
  }
});
const characterLimit = stitches.css({
  fontFamily: "$defaultFont",
  fontSize: "$75",
  marginTop: "$100"
});
const srOnly = stitches.css({ srOnly: "true" });
const EzField = forwardRef((props, ref) => {
  const generatedId = useId();
  const id = props.id || generatedId;
  const labelId = useId();
  const { helperText, label, touched, error, type = "text", maxLength, labelHidden } = props;
  const Input = resolveInputFromType(type);
  const showInlineError = inlineElements.includes(type);
  const [focused, { onBlur, onFocus }] = useFocus();
  const [hovered, mouseEvents] = useHover();
  const { value, onChange } = useInput(props.value || "");
  const active = focused || hovered;
  const showError = Boolean(touched && error);
  const labelType = "label";
  const errorMessageId = `errorMessage-${id}`;
  const helperTextId = `helperText-${id}`;
  const errorMessage = /* @__PURE__ */ jsx("div", { className: errorContainer(), children: /* @__PURE__ */ jsx(
    "span",
    {
      id: errorMessageId,
      className: clsx(errorCallout({ active, inline: showInlineError }), arrow()),
      children: error
    }
  ) });
  const relative = { position: "relative" };
  return /* @__PURE__ */ jsxs(
    "div",
    {
      className: clsx(
        "EzField",
        props.classNameSuffix && `EzField-${props.classNameSuffix}`,
        field({ labelSize: props.labelSize })
      ),
      ...mouseEvents,
      children: [
        !labelHidden && /* @__PURE__ */ jsxs("div", { children: [
          /* @__PURE__ */ jsxs("div", { style: relative, children: [
            /* @__PURE__ */ jsx(
              EzLabel,
              {
                id: labelId,
                htmlFor: id,
                as: labelType,
                error: showError,
                use: props.labelSize === "small" ? "secondary" : "primary",
                children: label
              }
            ),
            !showInlineError && showError && /* @__PURE__ */ jsx(ErrorIcon, {})
          ] }),
          !showInlineError && showError && errorMessage
        ] }),
        helperText && /* @__PURE__ */ jsx("div", { id: helperTextId, className: helper(), children: helperText }),
        /* @__PURE__ */ jsxs("div", { children: [
          /* @__PURE__ */ jsxs("div", { style: relative, children: [
            /* @__PURE__ */ jsx(
              Input,
              {
                ...{
                  ...props,
                  ...wrapEvents(props, { onBlur, onFocus, onChange }),
                  id,
                  name: props.name || id,
                  type,
                  "aria-labelledby": labelId,
                  "aria-invalid": showError,
                  "aria-describedby": clsx(showError && errorMessageId, helperText && helperTextId),
                  ref,
                  showInlineError: showInlineError && showError || void 0
                }
              }
            ),
            showInlineError && showError && /* @__PURE__ */ jsx(ErrorIcon, {})
          ] }),
          showInlineError && showError && errorMessage
        ] }),
        "maxLength" in props && typeof value === "string" && /* @__PURE__ */ jsxs("div", { className: characterLimit(), children: [
          value.length,
          "/",
          maxLength
        ] }),
        labelHidden && /* @__PURE__ */ jsx("div", { className: srOnly(), id: labelId, children: label })
      ]
    }
  );
});
EzField.displayName = "EzField";
export {
  EzField as default
};
//# sourceMappingURL=EzField.es.js.map
